import { useState } from 'react';
import {
    LayoutDashboard,
    Settings,
    ChevronDown,
    Plus,
    Building,
    User,
    Users,
    LogOut,
    Menu,
    X,
    Database,
    UserPlus,
    HelpCircle,
    Briefcase as BriefcaseIcon,
    MessageSquare,
    FolderOpen,
    Rocket,
} from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { useWorkspace } from '../contexts/WorkspaceContext';
import { ThemeSwitcherDropdown } from './ThemeSwitcher';
import { useTranslation } from 'react-i18next';
import { useTutorial } from '../contexts/TutorialContext';
import HRLogo from './HRLogo';

interface AppLayoutProps {
    children: React.ReactNode;
    currentView: string;
    onNavigate: (view: string) => void;
    /** When true, removes padding from <main> so children can fill edge-to-edge (e.g. chat view) */
    isFullBleed?: boolean;
}

export default function AppLayout({ children, currentView, onNavigate, isFullBleed }: AppLayoutProps) {
    const { user, logout, isSuperAdmin } = useAuth();
    const { workspaces, currentWorkspace, setCurrentWorkspace, userRole } = useWorkspace();
    const [showWorkspaceSwitcher, setShowWorkspaceSwitcher] = useState(false);
    const [showMobileNav, setShowMobileNav] = useState(false);
    const { t } = useTranslation('dashboard');
    const tutorial = useTutorial();

    // ════════════════════════════════════════════════════════════════════
    // 4-ANCHOR NAVIGATION — Governed AI Infrastructure
    // Dashboard → Govern  |  Work → Deploy  |  Data → Sovereignty  |  Settings → Infrastructure
    // ════════════════════════════════════════════════════════════════════
    const navItemIds = [
        { id: 'dashboard', labelKey: 'nav.dashboard', icon: LayoutDashboard, show: true, mode: 'GOVERN' },
        { id: 'command-center', labelKey: 'nav.command_center', icon: Rocket, show: true, mode: 'OPERATE' },
        { id: 'briefcase', labelKey: 'nav.briefcase', icon: FolderOpen, show: true, mode: 'DOCUMENTS' },
        { id: 'messages', labelKey: 'nav.messages', icon: MessageSquare, show: true, mode: 'COMMUNICATE' },
        { id: 'social', labelKey: 'nav.social', icon: Users, show: true, mode: 'NETWORK' },
        { id: 'data', labelKey: 'nav.data', icon: Database, show: true, mode: 'SOVEREIGNTY' },
        { id: 'settings', labelKey: 'nav.settings', icon: Settings, show: true, mode: 'INFRA' },
    ].filter(item => item.show);

    // Map sub-views to their parent nav anchor for active-state highlighting
    const viewToAnchor: Record<string, string> = {
        'dashboard': 'dashboard',
        'my-replica': 'dashboard',
        'company-replicas': 'dashboard',
        'teams': 'dashboard',
        'hierarchy': 'dashboard',
        'marketplace': 'dashboard',
        'replica-directory': 'dashboard',
        'brain': 'dashboard',
        'training-rounds': 'dashboard',
        'task-management': 'dashboard',
        'escalation-queue': 'dashboard',
        'create-replica': 'dashboard',
        'feed-synthesis': 'social',
        'governance': 'dashboard',
        'command-center': 'command-center',
        'work': 'command-center',
        'work-mode': 'command-center',
        'daily-routines': 'command-center',
        'briefcase': 'briefcase',
        'messages': 'messages',
        'social': 'social',
        'data': 'data',
        'my-data': 'data',
        'permissions': 'data',
        'audit-logs': 'data',
        'settings': 'settings',
        'integrations': 'settings',
        'billing': 'settings',
        'identity-verification': 'settings',
    };

    // Resolve active anchor — handles dynamic prefixed views too
    const getActiveAnchor = (): string => {
        if (viewToAnchor[currentView]) return viewToAnchor[currentView];
        // Handle prefixed views like chat-xxx, training-xxx, tools-xxx etc.
        if (currentView.startsWith('chat-')) return 'messages';
        if (currentView.startsWith('social-')) return 'social';
        if (currentView.startsWith('tools-') || currentView.startsWith('identity-') || currentView.startsWith('edit-replica-') || currentView.startsWith('training-') || currentView.startsWith('employment-')) return 'dashboard';
        return 'dashboard';
    };

    const activeAnchor = getActiveAnchor();

    // Mode labels for sidebar sublabel
    const modeLabels: Record<string, string> = {
        GOVERN: 'Govern',
        OPERATE: 'Operate',
        DEPLOY: 'Deploy',
        DOCUMENTS: 'Documents',
        COMMUNICATE: 'Chat',
        NETWORK: 'Network',
        SOVEREIGNTY: 'Sovereignty',
        INFRA: 'Infrastructure',
    };

    // Determine workspace icon based on whether it has a company attached
    const isCompanyWorkspace = (ws: { companyId?: string; type?: string }) => {
        return !!(ws.companyId || ws.type === 'COMPANY');
    };
    const getWorkspaceIcon = (ws: { companyId?: string; type?: string }) => {
        return isCompanyWorkspace(ws) ? Building : User;
    };

    return (
        <div className="min-h-screen relative flex">
            {/* Gradient background — matches marketing home page */}
            <div className="fixed inset-0 z-0 overflow-hidden bg-hig-bg-primary pointer-events-none">
                <div className="absolute inset-0 bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 dark:opacity-100 opacity-0"></div>
                <div className="absolute inset-0 bg-gradient-to-br from-stone-50 via-amber-50/30 to-stone-100 dark:opacity-0 opacity-100"></div>
            </div>

            {/* Sidebar — always visible on desktop */}
            <aside className={`
                fixed inset-y-0 left-0 z-50 w-64 bg-hig-bg-secondary/80 backdrop-blur-xl border-r border-hig-border
                transform transition-transform duration-200 ease-in-out
                lg:relative lg:translate-x-0
                ${showMobileNav ? 'translate-x-0' : '-translate-x-full'}
            `}>
                {/* Logo */}
                <div className="h-16 flex items-center justify-between px-4 border-b border-hig-divider">
                    <div className="flex items-center gap-2">
                        <button
                            onClick={() => onNavigate('home')}
                            className="flex items-center gap-2 hover:opacity-80 transition-opacity cursor-pointer"
                        >
                            <HRLogo size={26} state="default" showGlow={false} />
                            <span className="text-[15px] font-medium tracking-tight">
                                <span className="text-hig-text-primary">HumanReplica</span>
                                <span className="text-hig-accent">.ai</span><span className="text-[15px] text-hig-text-secondary -translate-y-[3px] inline-block ml-[1px] font-normal">®</span>
                            </span>
                        </button>
                    </div>
                    <div className="flex items-center gap-1">
                        <ThemeSwitcherDropdown />
                        <button
                            className="lg:hidden text-hig-text-tertiary hover:text-hig-text-primary"
                            onClick={() => setShowMobileNav(false)}
                            aria-label="Close navigation menu"
                        >
                            <X size={20} />
                        </button>
                    </div>
                </div>

                {/* Workspace Switcher — with type badges */}
                <div className="p-4 border-b border-hig-divider" data-tutorial="workspace-switcher">
                    <button
                        onClick={() => setShowWorkspaceSwitcher(!showWorkspaceSwitcher)}
                        className="w-full flex items-center justify-between px-3 py-2 bg-hig-bg-tertiary rounded-lg hover:bg-hig-bg-elevated transition-colors"
                        aria-expanded={showWorkspaceSwitcher}
                        aria-haspopup="listbox"
                    >
                        <div className="flex items-center gap-2 min-w-0">
                            {currentWorkspace && isCompanyWorkspace(currentWorkspace) ? (
                                <Building size={16} className="text-orange-400 flex-shrink-0" />
                            ) : (
                                <User size={16} className="text-blue-400 flex-shrink-0" />
                            )}
                            <span className="text-sm text-hig-text-primary truncate">
                                {currentWorkspace?.name || t('nav.select_workspace')}
                            </span>
                            {/* Workspace type badge */}
                            {currentWorkspace && isCompanyWorkspace(currentWorkspace) ? (
                                <span className="text-[9px] px-1.5 py-0.5 rounded-full bg-orange-500/15 text-orange-400 border border-orange-500/20 font-bold uppercase tracking-wider flex-shrink-0">
                                    Enterprise
                                </span>
                            ) : (
                                <span className="text-[9px] px-1.5 py-0.5 rounded-full bg-blue-500/15 text-blue-400 border border-blue-500/20 font-bold uppercase tracking-wider flex-shrink-0">
                                    Personal
                                </span>
                            )}
                        </div>
                        <ChevronDown size={16} className={`text-hig-text-tertiary transition-transform flex-shrink-0 ${showWorkspaceSwitcher ? 'rotate-180' : ''}`} />
                    </button>

                    {/* Workspace Dropdown */}
                    {showWorkspaceSwitcher && (
                        <div className="mt-2 py-1 bg-hig-bg-elevated rounded-lg shadow-lg border border-hig-border">
                            {workspaces.map((ws) => {
                                const WsIcon = getWorkspaceIcon(ws);
                                return (
                                    <button
                                        key={ws.id}
                                        onClick={() => {
                                            setCurrentWorkspace(ws);
                                            setShowWorkspaceSwitcher(false);
                                            onNavigate('dashboard');
                                        }}
                                        className={`w-full px-3 py-2 flex items-center gap-2 text-left hover:bg-hig-bg-tertiary transition-colors ${currentWorkspace?.id === ws.id ? 'bg-hig-bg-tertiary' : ''
                                            }`}
                                    >
                                        <WsIcon size={14} className={isCompanyWorkspace(ws) ? 'text-orange-400' : 'text-blue-400'} />
                                        <span className="text-sm text-hig-text-primary truncate flex-1">{ws.name}</span>
                                        {isCompanyWorkspace(ws) ? (
                                            <span className="text-[8px] px-1 py-0.5 rounded bg-orange-500/10 text-orange-400/70 font-bold uppercase">ENT</span>
                                        ) : (
                                            <span className="text-[8px] px-1 py-0.5 rounded bg-blue-500/10 text-blue-400/70 font-bold uppercase">PER</span>
                                        )}
                                    </button>
                                );
                            })}
                            <div className="border-t border-hig-divider mt-1 pt-1">
                                <button
                                    onClick={() => {
                                        onNavigate('new-workspace');
                                        setShowWorkspaceSwitcher(false);
                                    }}
                                    className="w-full px-3 py-2 flex items-center gap-2 text-left hover:bg-hig-bg-tertiary transition-colors text-hig-accent"
                                >
                                    <Plus size={14} />
                                    <span className="text-sm">{t('nav.create_workspace')}</span>
                                </button>
                                <button
                                    onClick={() => {
                                        onNavigate('join-workspace');
                                        setShowWorkspaceSwitcher(false);
                                    }}
                                    className="w-full px-3 py-2 flex items-center gap-2 text-left hover:bg-hig-bg-tertiary transition-colors text-hig-text-secondary"
                                >
                                    <UserPlus size={14} />
                                    <span className="text-sm">{t('nav.join_workspace', 'Join Workspace')}</span>
                                </button>
                            </div>
                        </div>
                    )}
                </div>

                {/* Navigation — 4 Primary Anchors */}
                <nav className="p-4 space-y-1" aria-label="Main navigation">
                    {navItemIds.map((item) => {
                        const Icon = item.icon;
                        const isActive = activeAnchor === item.id;
                        return (
                            <button
                                key={item.id}
                                data-tutorial={`nav-${item.id}`}
                                onClick={() => {
                                    onNavigate(item.id);
                                    setShowMobileNav(false);
                                }}
                                className={`w-full flex items-center gap-3 px-3 py-3 rounded-lg transition-all group ${isActive
                                    ? 'bg-hig-accent/10 text-hig-accent shadow-sm shadow-hig-accent/5'
                                    : 'text-hig-text-secondary hover:text-hig-text-primary hover:bg-hig-bg-tertiary'
                                    }`}
                                aria-current={isActive ? 'page' : undefined}
                            >
                                <Icon size={18} />
                                <div className="flex flex-col items-start">
                                    <span className="text-sm font-semibold">{t(item.labelKey)}</span>
                                    <span className={`text-[10px] font-medium uppercase tracking-wider ${isActive ? 'text-hig-accent/60' : 'text-hig-text-tertiary'}`}>
                                        {modeLabels[item.mode]}
                                    </span>
                                </div>
                            </button>
                        );
                    })}
                </nav>

                {/* User Menu */}
                <div className="absolute bottom-0 left-0 right-0 p-4 border-t border-hig-divider">
                    <div className="flex items-center gap-3">
                        <div className="w-9 h-9 rounded-full bg-hig-accent/15 flex items-center justify-center">
                            <span className="text-hig-accent text-sm font-medium">
                                {user?.email?.[0]?.toUpperCase() || 'U'}
                            </span>
                        </div>
                        <div className="flex-1 min-w-0">
                            <p className="text-sm text-hig-text-primary truncate">{user?.email}</p>
                            {isSuperAdmin ? (
                                <span className="inline-flex items-center gap-1 text-[10px] px-1.5 py-0.5 rounded-full bg-gradient-to-r from-amber-500/20 to-orange-500/20 text-amber-400 border border-amber-500/30 font-bold uppercase tracking-wider">
                                    ⚡ Super Admin
                                </span>
                            ) : (
                                <p className="text-xs text-hig-text-tertiary">
                                    {userRole ? t(`nav.role_${userRole}`, userRole) : t('nav.free_plan')}
                                </p>
                            )}
                        </div>

                        <button
                            onClick={logout}
                            className="p-2 text-hig-text-tertiary hover:text-hig-text-primary hover:bg-hig-bg-tertiary rounded-lg transition-colors"
                            title={t('nav.sign_out')}
                            aria-label={t('nav.sign_out')}
                        >
                            <LogOut size={18} />
                        </button>
                    </div>
                    {/* Tutorial help button */}
                    <button
                        onClick={() => tutorial.startTutorial()}
                        className="mt-3 w-full flex items-center gap-2.5 px-3 py-2 rounded-xl text-hig-text-tertiary hover:text-hig-accent hover:bg-hig-accent/5 transition-colors"
                    >
                        <HelpCircle size={16} />
                        <span className="text-xs font-medium">{t('tutorial.help_button')}</span>
                    </button>
                </div>
            </aside>

            {/* Main Content */}
            <div className="flex-1 flex flex-col min-w-0 relative z-10">
                {/* Mobile Header */}
                <header className="lg:hidden h-16 bg-hig-bg-secondary/80 backdrop-blur-xl border-b border-hig-border flex items-center justify-between px-4">
                    <button
                        onClick={() => setShowMobileNav(true)}
                        className="text-hig-text-secondary hover:text-hig-text-primary"
                        aria-label="Open navigation menu"
                        aria-expanded={showMobileNav}
                    >
                        <Menu size={24} />
                    </button>
                    <div className="flex items-center gap-2">
                        <HRLogo size={26} state="default" showGlow={false} />
                        <span className="text-[15px] font-medium tracking-tight">
                            <span className="text-hig-text-primary">HumanReplica</span>
                            <span className="text-hig-accent">.ai</span><span className="text-[15px] text-hig-text-secondary -translate-y-[3px] inline-block ml-[1px] font-normal">®</span>
                        </span>
                    </div>
                    <ThemeSwitcherDropdown />
                </header>

                {/* Content — isFullBleed removes padding for edge-to-edge views like chat */}
                <main className={`flex-1 overflow-auto ${isFullBleed ? '' : 'p-4 lg:p-6'}`}>
                    {children}
                </main>
            </div>

            {/* Mobile Overlay */}
            {showMobileNav && (
                <div
                    className="fixed inset-0 bg-black/50 z-40 lg:hidden"
                    onClick={() => setShowMobileNav(false)}
                />
            )}
        </div>
    );
}
